<template>
    <div class="config-competitor-create-one">
        <div class="config-competitor-card rounder-8 mb-4">
            <div class="product-info-header mb-3">
                <span class="product-info-label">Cliente: {{ authentication_user.client_name }}</span>
            </div>

            <div class="mb-4">
                <label class="form-label fw-600 size-13 text-secondary mb-2">SKU DEL PRODUCTO</label>
                <input 
                    type="text" 
                    class="form-control fw-500 size-14" 
                    v-model="productSku"
                    placeholder="Ingresa el SKU del producto"
                />
            </div>

            <div class="position-cards-grid mb-4">
                <div 
                    v-for="option in positioningOptions" 
                    :key="option.value"
                    class="position-card"
                    :class="{ 'position-card--active': positioning === option.value }"
                    @click="positioning = option.value"
                >
                    <div class="position-card-icon">
                        <font-awesome-icon :icon="['fas', option.icon]" />
                    </div>
                    <div class="position-card-title">{{ option.title }}</div>
                    <div class="position-card-desc">{{ option.desc }}</div>
                </div>
            </div>

            <div 
                class="mb-4 w-100"
                v-if="positioning != 1 && positioning != 3"
            >
                <label class="form-label fw-600 size-13 text-secondary mb-2">DIFERENCIAL FRENTE AL PRECIO DE MERCADO</label>
                <div class="input-group">
                    <input 
                        type="number" 
                        class="form-control fw-600 size-16" 
                        v-model.number="targetDifferential"
                        placeholder="0.000"
                        step="0.001"
                        min="0"
                        @keydown="onKeyDownPercent"
                        @input="formatPercent"
                    />
                    <span class="input-group-text bg-light border-start-0 text-secondary fw-500">% vs. mercado</span>
                </div>
                <small class="text-secondary size-12 mt-1 d-block">Formato decimal: 0.500 = 50%, 0.990 = 99%</small>
            </div>

            <div class="competitors-section">
                <label class="form-label fw-600 size-13 text-secondary mb-2">COMPETIDORES DE REFERENCIA</label>
                <div class="competitors-badges-list">
                    <span 
                        v-for="competitor in competitors" 
                        :key="competitor.id"
                        class="competitor-badge"
                        :class="{ 'competitor-badge--selected': isSelectedCompetitor(competitor.id) }"
                        @click="toggleCompetitor(competitor.id)"
                    >
                        {{ competitor.competitor_name }}
                    </span>
                </div>
            </div>

            <div class="internal-competitors-section mt-4" v-if="selectedInternalCompetitors.length > 0">
                <label class="form-label fw-600 size-13 text-secondary mb-2">SKU COMPETIDOR INTERNO</label>
                <div 
                    v-for="competitor in selectedInternalCompetitors" 
                    :key="'internal-' + competitor.id"
                    class="internal-competitor-input mb-3"
                >
                    <div class="internal-competitor-label">{{ competitor.competitor_name }}</div>
                    <input 
                        type="text" 
                        class="form-control fw-500 size-14" 
                        v-model="internalCompetitorSkus[competitor.id]"
                        placeholder="Ingresa el SKU del competidor interno"
                    />
                </div>
            </div>
        </div>

        <div class="d-flex justify-content-end gap-2">
            <button class="btn btn-success fw-400 px-4" @click="saveCompetitor" :disabled="saving || !productSku">
                <span v-if="saving">Guardando...</span>
                <span v-else>Guardar</span>
            </button>
        </div>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import competitorInternalMixin from './mixins/competitorInternalMixin';

const POSITIONING_OPTIONS = [
    { value: 0, title: 'Por debajo del mercado', desc: 'Precio agresivo, ganar volumen', icon: 'arrow-down' },
    { value: 1, title: 'Paridad con mercado', desc: 'Alineado a la competencia', icon: 'balance-scale' },
    { value: 2, title: 'Por encima del mercado', desc: 'Premium / diferenciación', icon: 'arrow-up' },
    { value: 3, title: 'Precio fijo', desc: 'No se mueve por competidor', icon: 'lock' },
];

export default {
    name: 'config-competitor-create-one',
    mixins: [competitorInternalMixin],
    props: {
        propHead: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            positioningOptions: POSITIONING_OPTIONS,
            positioning: 3,
            targetDifferential: 0.000,
            productSku: '',
            saving: false,
            competitors: [],
            selectedCompetitorIds: [],
            internalCompetitorSkus: {},
        };
    },
    computed: {
        selectedInternalCompetitors() {
            return this.competitors.filter(comp => 
                this.selectedCompetitorIds.includes(comp.id) && 
                this.isInternalCompetitor(comp.competitor_name)
            );
        }
    },
    created() {
        this.getCompetitors();
    },
    methods: {
        ...mapActions(['get', 'store']),
        isSelectedCompetitor(id) {
            return this.selectedCompetitorIds.includes(id);
        },
        toggleCompetitor(id) {
            const competitor = this.competitors.find(c => c.id === id);
            const isInternal = competitor && this.isInternalCompetitor(competitor.competitor_name);
            const isCurrentlySelected = this.selectedCompetitorIds.includes(id);

            if (isInternal && !isCurrentlySelected) {
                const hasInternalSelected = this.selectedInternalCompetitors.length > 0;
                if (hasInternalSelected) {
                    this.$notification.warning('Solo puedes seleccionar un competidor interno.', { timer: 5, showCloseIcn: true });
                    return;
                }
            }

            if (isCurrentlySelected) {
                this.selectedCompetitorIds.splice(this.selectedCompetitorIds.indexOf(id), 1);
            } else {
                this.selectedCompetitorIds.push(id);
            }
        },
        onKeyDownPercent(event) {
            if (event.key === '-') {
                event.preventDefault();
            }
        },
        formatPercent(event) {
            const rawValue = event.target.value;
            const sanitizedValue = String(rawValue).replace(/[^0-9.]/g, '');
            const parsedValue = parseFloat(sanitizedValue);

            if (!isNaN(parsedValue)) {
                let value = parsedValue;
                if (value > 1) {
                    value = value / 100;
                }
                this.targetDifferential = Number(value.toFixed(3));
            } else {
                this.targetDifferential = 0;
            }
        },
        getCompetitors() {
            let url = `/api/pricescrapingcompetitors/`;
            if (!this.authentication_user.client_channel) {
                url = url + `?business=${this.authentication_user.client_business_select}`;
            }
            this.get({ url }).then((response) => {
                this.competitors = response.results;
            });
        },
        saveCompetitor() {
            if (!this.productSku) {
                this.$notification.warning('Debes ingresar el SKU del producto.', { timer: 5, showCloseIcn: true });
                return;
            }
            if (this.selectedCompetitorIds.length === 0) {
                this.$notification.warning('Debes seleccionar al menos un competidor.', { timer: 5, showCloseIcn: true });
                return;
            }

            this.saving = true;

            const codexProductCompetitor = this.selectedInternalCompetitors.length > 0
                ? this.internalCompetitorSkus[this.selectedInternalCompetitors[0].id] || null
                : null;

            const data = {
                product: this.productSku,
                rule: this.positioning,
                value: (this.positioning === 1 || this.positioning === 3) ? 0 : this.targetDifferential,
                client_competitor: this.selectedCompetitorIds,
                codex_product_competitor: codexProductCompetitor,
                convert: null,
                unit_convert: null,
                business: this.authentication_user.client_business_select || null,
            };

            this.store({
                url: `/api/pricescrapingrules/create_single_rule/`,
                params: data,
            }).then(() => {
                this.saving = false;
                this.$emit('refrest');
                this.$notification.success('Regla de competidor creada correctamente.', { timer: 5, showCloseIcn: true });
            }).catch((error) => {
                this.saving = false;
                if (error.status == 400) {
                    const errorMsg = error.data.message || error.data.error || 'Error al crear la regla.';
                    this.$notification.error(errorMsg, { timer: 5, showCloseIcn: true });
                } else {
                    this.$notification.error('Error al crear la regla de competidor.', { timer: 5, showCloseIcn: true });
                }
            });
        },
    }
};
</script>

<style scoped>
.config-competitor-card {
    background: #ffffff;
}

.product-info-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 0;
}

.product-info-label {
    font-size: 0.9rem;
    font-weight: 500;
    color: #374151;
}

.product-info-separator {
    color: #9ca3af;
}

.position-cards-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
}

.position-card {
    border: 2px solid #e5e7eb;
    border-radius: 10px;
    padding: 16px 12px;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s ease;
    background: #fff;
}

.position-card:hover {
    border-color: #1AA179;
}

.position-card--active {
    border-color: #1AA179;
    background: #e6f9f1;
}

.position-card-icon {
    font-size: 1.2rem;
    color: #6b7280;
    margin-bottom: 8px;
}

.position-card--active .position-card-icon {
    color: #1AA179;
}

.position-card-title {
    font-size: 0.85rem;
    font-weight: 600;
    color: #111827;
    margin-bottom: 4px;
}

.position-card-desc {
    font-size: 0.72rem;
    color: #9ca3af;
}

.input-group-text {
    background-color: #f8f9fa !important;
    border-color: #dee2e6 !important;
    font-size: 14px;
}

.form-control:focus {
    border-color: #1AA179;
    box-shadow: 0 0 0 0.2rem rgba(26, 161, 121, 0.25);
}

.competitors-section {
    margin-top: 8px;
}

.competitors-badges-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.competitor-badge {
    display: inline-block;
    background: #f3f4f6;
    color: #9ca3af;
    font-size: 0.82rem;
    font-weight: 600;
    padding: 6px 14px;
    border-radius: 999px;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 2px solid transparent;
}

.competitor-badge:hover {
    border-color: #1AA179;
}

.competitor-badge--selected {
    background: #1AA179;
    color: #fff;
    border-color: #1AA179;
}

.internal-competitors-section {
    border-top: 1px solid #e5e7eb;
    padding-top: 16px;
}

.internal-competitor-input {
    background: #f9fafb;
    padding: 12px;
    border-radius: 8px;
}

.internal-competitor-label {
    font-size: 0.85rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 8px;
}

.btn-success {
    background-color: #1AA179 !important;
    border-color: #1AA179 !important;
}

.btn-success:hover:not(:disabled) {
    background-color: #158a65 !important;
    border-color: #158a65 !important;
}

.btn-success:disabled {
    opacity: 0.65;
}
</style>
